/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef, useEffect, memo } from 'react';
import {
  Layers, X, Sparkles, Undo2, Redo2, Users, LayoutGrid
} from 'lucide-react';
import { FullProject, Task } from '../../types';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalTimerWidget } from '../GlobalTimerWidget';
import { NotificationCenter } from '../NotificationCenter';
import { AppHeaderSearchBar } from './AppHeaderSearchBar';
import { AppHeaderDataDropdown } from './AppHeaderDataDropdown';
import { AppHeaderProfileDropdown } from './AppHeaderProfileDropdown';
import { AppHeaderCloudSyncBadge } from './AppHeaderCloudSyncBadge';

export interface AppHeaderProps {
  // Search
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  searchResults?: any[];
  searchIndex?: number;
  setSearchIndex?: (index: number) => void;
  searchMode?: 'filter' | 'highlight';
  setSearchMode?: (mode: 'filter' | 'highlight') => void;
  searchCategory?: string;
  setSearchCategory?: (category: any) => void;
  showSearchDropdown: boolean;
  setShowSearchDropdown: (show: boolean) => void;
  handleSearchPrev: () => void;
  handleSearchNext: () => void;
  navigateToSearchResult: (index: number) => void;
  setActiveSearchTaskId?: (id: string | number | null) => void;
  lastNavigatedIndexRef: React.MutableRefObject<number>;

  // Filter Active Indicator
  isFilterActive: boolean;
  clearAllFilters: () => void;

  // Undo / Redo UI badge
  showUndo: boolean;
  setShowUndo: (show: boolean) => void;

  // Sidebars & UI Mode
  isRightSidebarOpen: boolean;
  toggleRightSidebar: () => void;
  uiMode?: 'standard' | 'expert';
  setUiMode?: (mode: 'standard' | 'expert') => void;

  // Data & Export/Import Dropdown
  showDataDropdown?: boolean;
  setShowDataDropdown?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExportProModal: (show: boolean) => void;
  exportAsJSON?: () => void;
  exportAsExcel?: () => void;

  // Notifications
  notifications: any[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  handleNavigateToNotificationTask: (projectId: string, taskId: string | number) => void;

  // Profile & User Dropdown
  showProfileDropdown: boolean;
  setShowProfileDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUserProfileModal: (show: boolean) => void;
  syncProjectViews: boolean;
  setSyncProjectViews: (sync: boolean | ((s: boolean) => boolean)) => void;
  setShowCreditsAndHelpModal: (show: boolean) => void;
  setShowGuidedTour: (show: boolean) => void;
  setShowPrivacyModal: (show: boolean) => void;

  // Optional Context overrides (resolved automatically from ProjectContext / UIContext / AuthContext if omitted)
  showProjectOverviewModal?: boolean;
  setShowProjectOverviewModal?: (show: boolean) => void;
  isSyncing?: boolean;
  pendingSyncCount?: number;
  isOnline?: boolean;
  syncError?: any;
  lastSynced?: number | string | null;
  syncStatusText?: string;
  allProjects?: FullProject[];
  allTasksGlobal?: Task[];
  updateTask?: (id: string | number, updates: Partial<Task>, projectId?: string) => void;
  setEditingTask?: (task: Task | null) => void;
  undo?: () => void;
  redo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  showChatPanel?: boolean;
  setShowChatPanel?: React.Dispatch<React.SetStateAction<boolean>>;
  importFromJSON?: (file: File) => Promise<any>;
  user?: any;
  userRole?: string | null;
  signOut?: () => Promise<void>;
  isDarkMode?: boolean;
  setIsDarkMode?: (dark: boolean | ((d: boolean) => boolean)) => void;
}

export const AppHeader: React.FC<AppHeaderProps> = memo((props) => {
  const projectCtx = useProject();
  const uiCtx = useUI();
  const authCtx = useAuth();

  const {
    showSearchDropdown,
    setShowSearchDropdown,
    handleSearchPrev,
    handleSearchNext,
    navigateToSearchResult,
    lastNavigatedIndexRef,
    isFilterActive,
    clearAllFilters,
    showUndo,
    setShowUndo,
    isRightSidebarOpen,
    toggleRightSidebar,
    setShowExportProModal,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    handleNavigateToNotificationTask,
    showProfileDropdown,
    setShowProfileDropdown,
    setShowUserProfileModal,
    syncProjectViews,
    setSyncProjectViews,
    setShowCreditsAndHelpModal,
    setShowGuidedTour,
    setShowPrivacyModal,
    syncStatusText: externalSyncStatusText,
  } = props;

  const searchQuery = props.searchQuery ?? uiCtx.searchQuery;
  const setSearchQuery = props.setSearchQuery ?? uiCtx.setSearchQuery;
  const searchResults = props.searchResults ?? uiCtx.searchResults;
  const searchIndex = props.searchIndex ?? uiCtx.searchIndex;
  const setSearchIndex = props.setSearchIndex ?? uiCtx.setSearchIndex;
  const searchMode = props.searchMode ?? uiCtx.searchMode;
  const setSearchMode = props.setSearchMode ?? uiCtx.setSearchMode;
  const searchCategory = props.searchCategory ?? uiCtx.searchCategory;
  const setSearchCategory = props.setSearchCategory ?? uiCtx.setSearchCategory;
  const setActiveSearchTaskId = props.setActiveSearchTaskId ?? uiCtx.setActiveSearchTaskId;
  const uiMode = props.uiMode ?? uiCtx.uiMode;
  const setUiMode = props.setUiMode ?? uiCtx.setUiMode;
  const showDataDropdown = props.showDataDropdown ?? uiCtx.showDataDropdown;
  const setShowDataDropdown = props.setShowDataDropdown ?? uiCtx.setShowDataDropdown;
  const exportAsJSON = props.exportAsJSON ?? projectCtx.exportAsJSON;
  const exportAsExcel = props.exportAsExcel ?? projectCtx.exportAsExcel;

  const showProjectOverviewModal = props.showProjectOverviewModal ?? uiCtx.showProjectOverviewModal;
  const setShowProjectOverviewModal = props.setShowProjectOverviewModal ?? uiCtx.setShowProjectOverviewModal;
  const isSyncing = props.isSyncing ?? projectCtx.isSyncing;
  const pendingSyncCount = props.pendingSyncCount ?? projectCtx.pendingSyncCount;
  const isOnline = props.isOnline ?? projectCtx.isOnline;
  const syncError = props.syncError ?? projectCtx.syncError;
  const lastSynced = props.lastSynced ?? projectCtx.lastSynced;
  const allProjects = props.allProjects ?? projectCtx.allProjects;
  const allTasksGlobal = props.allTasksGlobal ?? projectCtx.allTasksGlobal;
  const updateTask = props.updateTask ?? projectCtx.updateTask;
  const setEditingTask = props.setEditingTask ?? uiCtx.setEditingTask;
  const undo = props.undo ?? projectCtx.undo;
  const redo = props.redo ?? projectCtx.redo;
  const canUndo = props.canUndo ?? projectCtx.canUndo;
  const canRedo = props.canRedo ?? projectCtx.canRedo;
  const showChatPanel = props.showChatPanel ?? uiCtx.showChatPanel;
  const setShowChatPanel = props.setShowChatPanel ?? uiCtx.setShowChatPanel;
  const importFromJSON = props.importFromJSON ?? projectCtx.importFromJSON;
  const user = props.user ?? authCtx.user;
  const userRole = props.userRole ?? authCtx.userRole;
  const signOut = props.signOut ?? authCtx.signOut;
  const isDarkMode = props.isDarkMode ?? uiCtx.isDarkMode;
  const setIsDarkMode = props.setIsDarkMode ?? uiCtx.setIsDarkMode;

  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const dataDropdownRef = useRef<HTMLDivElement>(null);

  // Click-outside listener for header dropdowns
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(target)) {
        setShowProfileDropdown(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(target)) {
        setShowSearchDropdown(false);
      }
      if (showDataDropdown && dataDropdownRef.current && !dataDropdownRef.current.contains(target)) {
        setShowDataDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDataDropdown, setShowDataDropdown, setShowProfileDropdown, setShowSearchDropdown]);

  return (
    <nav className="bg-slate-950 text-white h-[64px] flex items-center px-4 gap-4 z-[100] fixed top-0 w-full shadow-lg border-b border-white/5 print:hidden">
      <div className="flex items-center gap-4 shrink-0 px-2">
        <div
          className={`flex items-center gap-3 group cursor-pointer px-3 py-1.5 rounded-2xl transition-all duration-200 ${
            showProjectOverviewModal
              ? 'bg-indigo-500/15 ring-2 ring-indigo-500/30'
              : 'hover:bg-white/5'
          }`}
          onClick={() => setShowProjectOverviewModal(!showProjectOverviewModal)}
        >
          <div className="w-10 h-10 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:scale-110 transition-transform">
            <Layers size={20} className="text-white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-black tracking-tighter text-white uppercase italic leading-tight">STRATUM</h1>
            <p className="text-[10px] font-bold text-slate-400 -mt-1 uppercase tracking-[0.2em]">Projektmanagement</p>
          </div>
        </div>

        <div className="h-4 w-px bg-white/10 mx-2" />

        {/* Cloud Sync Status (Farbpunkt mit Tooltip) */}
        <AppHeaderCloudSyncBadge
          externalSyncStatusText={externalSyncStatusText}
          pendingSyncCount={pendingSyncCount}
          syncError={syncError}
          isSyncing={isSyncing}
          isOnline={isOnline}
          lastSynced={lastSynced}
        />

        {/* Global Search Bar */}
        <AppHeaderSearchBar
          searchDropdownRef={searchDropdownRef}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          searchIndex={searchIndex}
          setSearchIndex={setSearchIndex}
          searchMode={searchMode}
          setSearchMode={setSearchMode}
          searchCategory={searchCategory}
          setSearchCategory={setSearchCategory}
          showSearchDropdown={showSearchDropdown}
          setShowSearchDropdown={setShowSearchDropdown}
          handleSearchPrev={handleSearchPrev}
          handleSearchNext={handleSearchNext}
          navigateToSearchResult={navigateToSearchResult}
          setActiveSearchTaskId={setActiveSearchTaskId}
          lastNavigatedIndexRef={lastNavigatedIndexRef}
          allProjects={allProjects}
        />
      </div>

      <GlobalTimerWidget
        allTasks={allTasksGlobal}
        allProjects={allProjects}
        updateTask={updateTask as any}
        onTaskSelect={(task) => setEditingTask(task)}
      />

      <div className="flex-1" />

      {/* Filter Active Indicator */}
      {isFilterActive && (
        <div className="flex items-center gap-1.5 px-3 h-8 bg-indigo-500/20 border border-indigo-500/30 rounded-xl mr-2">
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">Filter aktiv</span>
          <button
            onClick={clearAllFilters}
            className="p-0.5 hover:bg-white/10 rounded-md text-indigo-300"
            title="Alle Filter zurücksetzen"
          >
            <X size={12} strokeWidth={3} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 pr-4">
        {showUndo && (
          <div
            className="flex items-center gap-1.5 px-3 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-[10px] font-black uppercase tracking-widest animate-pulse shrink-0"
            title="Klicken Sie auf das Pfeil-Symbol rechts, um diese Aktion rückgängig zu machen"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span>Aktion ausgeführt</span>
          </div>
        )}

        <div className="flex items-center gap-1 p-1 bg-white/5 rounded-xl border border-white/10">
          <button
            onClick={() => {
              undo();
              setShowUndo(false);
            }}
            disabled={!canUndo}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-20 transition-all active:scale-95"
            title="Rückgängig (Strg+Z / Cmd+Z)"
            aria-label="Rückgängig machen"
          >
            <Undo2 size={18} />
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-20 transition-all active:scale-95"
            title="Wiederholen (Strg+Y / Cmd+Shift+Z)"
            aria-label="Wiederholen"
          >
            <Redo2 size={18} />
          </button>
        </div>

        <button
          onClick={() => setShowChatPanel(!showChatPanel)}
          className={`p-1.5 rounded-xl transition-all active:scale-95 border ${showChatPanel ? 'bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-500/20' : 'bg-white/5 text-purple-400 hover:text-white hover:bg-white/10 border-white/10'}`}
          title="KI Assistent"
        >
          <Sparkles size={18} />
        </button>

        <div className="h-6 w-px bg-white/10 mx-1" />

        {/* Smart Adaptive UI Switcher: Standard vs. Experte */}
        <div className="flex bg-white/5 p-0.5 rounded-xl border border-white/10 h-[34px] items-center gap-0.5" title="Ansichts-Modus wechseln">
          <button
            onClick={() => setUiMode('standard')}
            className={`px-2.5 h-full rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 ${
              uiMode === 'standard'
                ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-400/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            title="Standardmodus: Aufgeräumte Kernfunktionen für den schnellen Arbeitsalltag"
          >
            <LayoutGrid size={13} />
            <span className="hidden xl:inline">Standard</span>
          </button>
          <button
            onClick={() => setUiMode('expert')}
            className={`px-2.5 h-full rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 ${
              uiMode === 'expert'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm ring-1 ring-purple-400/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            title="Expertenmodus: Vollständiges Management-Arsenal (SWOT, RACI, Tiefen-Metriken, Dokumente)"
          >
            <Sparkles size={13} />
            <span className="hidden xl:inline">Experte</span>
          </button>
        </div>

        <AppHeaderDataDropdown
          dataDropdownRef={dataDropdownRef}
          showDataDropdown={showDataDropdown}
          setShowDataDropdown={setShowDataDropdown}
          setShowExportProModal={setShowExportProModal}
          exportAsJSON={exportAsJSON}
          importFromJSON={importFromJSON}
          exportAsExcel={exportAsExcel}
        />

        {/* Notification Center */}
        <NotificationCenter
          notifications={notifications}
          unreadCount={unreadCount}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onNavigateToTask={handleNavigateToNotificationTask}
        />

        <AppHeaderProfileDropdown
          profileDropdownRef={profileDropdownRef}
          user={user}
          userRole={userRole}
          signOut={signOut}
          showProfileDropdown={showProfileDropdown}
          setShowProfileDropdown={setShowProfileDropdown}
          setShowUserProfileModal={setShowUserProfileModal}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          syncProjectViews={syncProjectViews}
          setSyncProjectViews={setSyncProjectViews}
          setShowCreditsAndHelpModal={setShowCreditsAndHelpModal}
          setShowGuidedTour={setShowGuidedTour}
          setShowPrivacyModal={setShowPrivacyModal}
        />

        <div className="h-6 w-px bg-white/10 mx-0.5" />

        {/* Personen-Panel (Zuweisung & Filter) - ganz rechts direkt über der Personenleiste */}
        <button
          onClick={toggleRightSidebar}
          className={`p-1.5 rounded-xl transition-all active:scale-95 border flex items-center justify-center shrink-0 ${
            isRightSidebarOpen
              ? 'bg-indigo-600 text-white border-indigo-600 shadow-[0_0_15px_rgba(79,70,229,0.4)] ring-1 ring-indigo-400/50'
              : 'bg-white/5 text-indigo-400 hover:text-white hover:bg-white/10 border-white/10'
          }`}
          title={isRightSidebarOpen ? 'Personenleiste schließen' : 'Personenleiste öffnen (Zuweisung & Filter)'}
          aria-label="Personenleiste umschalten"
          data-tour="people-sidebar-toggle"
        >
          <Users size={18} />
        </button>
      </div>
    </nav>
  );
});

AppHeader.displayName = 'AppHeader';
